import { ref, onMounted, reactive } from 'vue';
import TileLayer from 'ol/layer/Tile';
import { Group as LayerGroup, Vector as VectorLayer } from 'ol/layer';
import VectorSource from 'ol/source/Vector';
import { Stroke, Style } from 'ol/style';
import { getWidth, getTopLeft, buffer } from 'ol/extent';
import { GeoJSON } from 'ol/format';
import XYZ from 'ol/source/XYZ';
import { useDefaultsListStore } from '@/stores/defaultsListStore.js';
import { storeToRefs } from 'pinia';
import { get as getProjection } from 'ol/proj';
import WMTS from 'ol/source/WMTS';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import TileGrid from 'ol/tilegrid/TileGrid';
import TileWMS from 'ol/source/TileWMS';
import defaultTileLayers from '@/config/defaultTileLayers.js';
import { useMapToken } from '@/composables/useMapToken.js';

export default function useMapImagery ({
                                           map,
                                           osmLayer,
                                           imageService,
                                           aerialGroup,
                                           labelLayer,
                                           satelliteLayer,
                                           aerialLayer
                                       }) {
    const lgaClip = ref(null);
    const imageService2 = ref(new TileLayer({ visible: false }));
    const defaultsListStore = useDefaultsListStore();
    const { streetmap, client, imagery, homeCenter } = storeToRefs(defaultsListStore);

    const { mapToken, getMapToken } = useMapToken();

    const addStreetsBaseLayer = async (currentMap) => {
        if (streetmap.value) {
            const streetMaps = streetmap.value.filter((smap) => {
                return smap.map === currentMap || smap.map == null;
            });

            if (!streetMaps.length) {
                addDefaultSteetLayer();
            } else {
                if (streetMaps[0].provider === 'external-xyz') {
                    osmLayer.value = new TileLayer({
                        source: new XYZ({
                            url: streetMaps[0].url,
                            maxZoom: 20
                        }),
                        visible: true,
                        crossOrigin: 'anonymous'
                    });
                    map.value.addLayer(osmLayer.value);
                } else if (streetMaps[0].provider === 'geoserver') {
                    await getMapToken();
                    setInterval(() => {
                        getMapToken();
                    }, 5 * 60 * 1000);

                    osmLayer.value = new TileLayer({
                        visible: true,
                        crossOrigin: 'anonymous',
                        source: new XYZ({
                            tileLoadFunction: (tile, src) => {
                                tile.getImage().src = src + '?token=' + mapToken.value;
                            },
                            maxZoom: 20,
                            url: `https://mapping.forestree.app/map-tile/{z}/{x}/{y}.png`
                        })
                    });
                    map.value.addLayer(osmLayer.value);
                }
            }
        } else {
            addDefaultSteetLayer();
        }
    };

    const tileSourceFunction = async (imageTile, src) => {
        await getMapToken();
        return new XYZ({
            url: `https://mapping.forestree.app/map-tile/{z}/{x}/{y}.png?token=${mapToken.value}`,
            maxZoom: 20
        });
    };

    const addDefaultSteetLayer = (currentStormMap = null) => {
        if (currentStormMap) {
            osmLayer.value = new TileLayer({
                source: new XYZ({
                    url:
                        'https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?features=pois:disabled&style=lite.day&apiKey=XkfuVJOlIePZ07taQe6VNjIiNwIBU7wggerI9h7JUvs',
                    maxZoom: 20
                }),
                visible: true,
                crossOrigin: 'anonymous'
            });
        } else {
            osmLayer.value = defaultTileLayers().osmTileLayer;
        }
        map.value.addLayer(osmLayer.value);

    };

    const addAdditionalLayers = () => {
        if (client?.value?.identifier) {
            const lgaStyle = new Style({
                stroke: new Stroke({
                    color: 'rgb(33, 150, 243, 0.6)',
                    width: 4
                })
            });

            const lgaSource = new VectorSource({
                url: '/lga/' + client.value.identifier + '.geojson',
                format: new GeoJSON()
            });

            if (lgaSource) {
                lgaClip.value = new VectorLayer({
                    style: lgaStyle,
                    source: lgaSource
                });
                map.value.addLayer(lgaClip.value);

                lgaClip.value.getSource().on('addfeature', () => {
                    imageService.value.setExtent(buffer(lgaClip.value.getSource().getExtent(), 200));
                    imageService2.value.setExtent(buffer(lgaClip.value.getSource().getExtent(), 200));
                    labelLayer.value.setExtent(buffer(lgaClip.value.getSource().getExtent(), 200));
                });
            }
        }

        const showAerial = localStorage.getItem('aerial');

        if (showAerial === 'true') {
            osmLayer.value.setVisible(false);
        }
        if (imagery?.value) {
            handleImageryLayers(showAerial);
        } else if (homeCenter?.value?.tile) {
            handleHomeCenterTileLayers(showAerial);
        }
    };

    const handleImageryLayers = (showAerial) => {
        let imageUrl = '';
        let source = null;
        let source2 = null;
        let visState = false;
        let projExtent;
        let startResolution;
        let resolutions;
        let projection;
        let projectionExtent;
        let size;
        let matrixIds;
        let tileGrid;

        if (imageService.value.getVisible() || imageService2.value.getVisible() || showAerial === 'true') {
            visState = true;
        }

        switch (imagery.value.provider) {
            case 'metromap':
                imageUrl = `https://pathapi.metromap.com.au/api-link/${imagery.value.key}/wmts/Australian_capital_cities_latest/default/GoogleMapsCompatibleExt:epsg:3857/{z}/{y}/{x}.jpgpng`;
                source = new XYZ({
                    urls: [imageUrl],
                    tilePixelRatio: 1
                });
                break;
            case 'metromap-xyz':
                imageUrl = `https://api-v3.metromap.com.au/tiles/xyz/Australian_capital_cities_latest/{z}/{y}/{x}.jpgpng?key=${imagery.value.key}`;
                source = new XYZ({
                    urls: [imageUrl],
                    tilePixelRatio: 1
                });
                break;

            case 'metromap-wmts':
                projection = getProjection('EPSG:3857');
                projectionExtent = projection.getExtent();
                size = getWidth(projectionExtent) / 256;
                resolutions = new Array(22);
                matrixIds = new Array(22);
                for (let z = 0; z < 22; ++z) {
                    resolutions[z] = size / Math.pow(2, z);
                    matrixIds[z] = z;
                }

                source = new WMTS({
                    url: `https://api.metromap.com.au/ogc/key/${imagery.value.key}/service?SERVICE=WMTS`,
                    layer: 'Australian_capital_cities_latest',
                    matrixSet: 'webmercator',
                    format: 'image/png',
                    projection: projection,
                    tileGrid: new WMTSTileGrid({
                        origin: getTopLeft(projectionExtent),
                        resolutions: resolutions,
                        matrixIds: matrixIds
                    }),
                    style: 'default',
                    wrapX: true
                });
                break;
            case 'nearmap':
                imageUrl = `https://au0.nearmap.com/maps/z={z}&x={x}&y={y}&version=2&nml=Vert&client=wmts_integration&httpauth=false&apikey=${imagery.value.key}`;
                source = new XYZ({
                    urls: [imageUrl],
                    tilePixelRatio: 1
                });
                break;
            case 'esri':
                imageUrl = `${imagery.value.url}/tile/{z}/{y}/{x}`;
                source = new XYZ({
                    urls: [imageUrl],
                    tileSize: 512,
                    maxZoom: 19
                });
                break;
            case 'victoria':
                projExtent = getProjection('EPSG:3857').getExtent();
                startResolution = getWidth(projExtent) / 512;
                resolutions = new Array(22);
                for (let i = 0, ii = resolutions.length; i < ii; ++i) {
                    resolutions[i] = startResolution / Math.pow(2, i);
                }
                tileGrid = new TileGrid({
                    extent: projExtent,
                    resolutions: resolutions,
                    tileSize: [512, 512]
                });

                source = new TileWMS({
                    url: 'https://base.maps.vic.gov.au/service?',
                    params: { 'LAYERS': 'AERIAL_WM', 'TILED': true },
                    transition: 0,
                    tileGrid: tileGrid
                });
                break;
            case 'dual-wms':
                projExtent = getProjection('EPSG:3857').getExtent();
                startResolution = getWidth(projExtent) / 512;
                resolutions = new Array(22);
                for (let i = 0, ii = resolutions.length; i < ii; ++i) {
                    resolutions[i] = startResolution / Math.pow(2, i);
                }
                tileGrid = new TileGrid({
                    extent: projExtent,
                    resolutions: resolutions,
                    tileSize: [512, 512]
                });

                source = new TileWMS({
                    url: imagery.value.url,
                    transition: 0,
                    tileGrid: tileGrid,
                    format: 'image/png'
                });

                source2 = new TileWMS({
                    url: imagery.value.url2,
                    transition: 0,
                    tileGrid: tileGrid
                });
                break;
        }

        imageService.value = new TileLayer({
            source: source,
            visible: visState,
            crossOrigin: 'anonymous'
        });

        imageService2.value = new TileLayer({
            source: source2,
            visible: visState,
            crossOrigin: 'anonymous'
        });

        aerialGroup.value = new LayerGroup({
            layers: [imageService2.value, imageService.value, labelLayer.value]
        });

        labelLayer.value.setVisible(visState);
        const layers = map.value.getLayers();
        layers.insertAt(0, aerialGroup.value);

    };

    const handleHomeCenterTileLayers = (showAerial) => {
        let visState = false;

        if (imageService.value.getVisible() || imageService2.value.getVisible() || showAerial === 'true') {
            visState = true;
        }

        aerialLayer.value = new TileLayer({
            source: new XYZ({
                urls: [
                    `https://tile.forestree.dev/tile/${homeCenter.value.tile}/{z}/{x}/{y}.jpg`
                ],
                minZoom: 12,
                maxZoom: 20,
                tilePixelRatio: 1
            }),
            visible: visState
        });

        aerialGroup.value = new LayerGroup({
            layers: [aerialLayer.value, labelLayer.value]
        });

        labelLayer.value.setVisible(visState);
        const layers = map.value.getLayers();
        layers.insertAt(0, aerialGroup.value);
    };
    const baseMapSwitch = () => {
        if (osmLayer.value.getVisible()) {
            if (imagery.value) {
                imageService.value.setVisible(true);
                imageService2.value.setVisible(true); // need from map imagery mixin
                labelLayer.value.setVisible(true);
                localStorage.setItem('aerial', true);
            } else if (homeCenter.value.tile) {
                aerialLayer.value.setVisible(true);
                labelLayer.value.setVisible(true);
                localStorage.setItem('aerial', true);
            } else {
                satelliteLayer.value.setVisible(true);
                labelLayer.value.setVisible(true);

            }
            osmLayer.value.setVisible(false);
        } else {
            if (imagery.value) {
                imageService.value.setVisible(false);
                imageService2.value.setVisible(false); // need map imagery
                labelLayer.value.setVisible(false);
            } else if (homeCenter.value.tile) {
                aerialLayer.value.setVisible(false);
                labelLayer.value.setVisible(false);
            } else {
                satelliteLayer.value.setVisible(false);
                labelLayer.value.setVisible(false);
            }
            localStorage.removeItem('aerial');
            osmLayer.value.setVisible(true);
        }
    };

    return {
        imageService2,
        addStreetsBaseLayer,
        addAdditionalLayers,
        tileSourceFunction,
        baseMapSwitch
    };
}
